/**
 * The page's form fields, fillable in place.
 *
 * What the page shows is the field's own appearance, drawn by the server
 * into the PDF and rendered on the canvas -- so a filled field looks here
 * exactly as it will in Acrobat. The controls on top are see-through until
 * you type into one; then it shows a plain editor, and on leaving the field
 * the value is sent and the page re-rendered with its new appearance.
 */

import { useEffect, useState } from 'react'
import type { FormField, FormWidget } from '../api'

type Value = string | string[] | boolean | null

interface Props {
  fields: FormField[]
  pageIndex: number
  originX: number
  originY: number
  pageHeight: number
  scale: number
  /** Acrobat's "Highlight fields": a tint on every fillable field. */
  highlight: boolean
  onChange: (name: string, value: Value) => void
}

export function FormLayer(props: Props) {
  const { fields, pageIndex, originX, originY, pageHeight, scale } = props
  const place = (w: FormWidget) => {
    const [x0, y0, x1, y1] = w.rect
    return {
      left: (x0 - originX) * scale,
      top: (pageHeight - (y1 - originY)) * scale,
      width: (x1 - x0) * scale,
      height: (y1 - y0) * scale,
    }
  }

  return (
    <div className="page__layer form-layer" data-highlight={props.highlight || undefined}>
      {fields.flatMap((field) =>
        field.widgets
          .filter((w) => w.pageIndex === pageIndex && !w.hidden)
          .map((widget, i) => (
            <FieldControl
              key={`${field.name}:${i}`}
              field={field}
              widget={widget}
              style={place(widget)}
              scale={scale}
              onChange={props.onChange}
            />
          )),
      )}
    </div>
  )
}

function FieldControl({ field, widget, style, scale, onChange }: {
  field: FormField
  widget: FormWidget
  style: React.CSSProperties
  scale: number
  onChange: Props['onChange']
}) {
  const label = field.tooltip || field.name
  const common = {
    className: 'field',
    'data-kind': field.kind,
    'data-required': field.required || undefined,
    title: label + (field.required ? ' (required)' : ''),
    'aria-label': label,
    disabled: field.readOnly,
    onClick: (event: React.MouseEvent) => event.stopPropagation(),
  }

  if (field.kind === 'checkbox' || field.kind === 'radio') {
    const on = widget.onState !== null && field.value === widget.onState
    return (
      <button
        {...common}
        role={field.kind}
        aria-checked={on}
        style={style}
        onClick={(event) => {
          event.stopPropagation()
          if (field.kind === 'checkbox') onChange(field.name, on ? false : widget.onState)
          else if (!on) onChange(field.name, widget.onState)
        }}
      />
    )
  }

  if (field.kind === 'combo' || field.kind === 'list') {
    return <ChoiceControl field={field} style={style} common={common} onChange={onChange} />
  }

  if (field.kind === 'text') {
    return <TextControl field={field} style={style} scale={scale} common={common} onChange={onChange} />
  }
  return null         // push buttons and signature fields are not filled here
}

function TextControl({ field, style, scale, common, onChange }: {
  field: FormField
  style: React.CSSProperties
  scale: number
  common: Record<string, unknown>
  onChange: Props['onChange']
}) {
  const saved = typeof field.value === 'string' ? field.value : ''
  const [draft, setDraft] = useState(saved)
  useEffect(() => setDraft(saved), [saved])

  const height = Number(style.height) || 16
  const fontSize = field.fontSize > 0
    ? field.fontSize * scale
    : Math.min(Math.max(height * (field.multiline ? 0.3 : 0.6), 8), 14 * scale)
  const commit = () => {
    if (draft !== saved) onChange(field.name, draft)
  }
  const shared = {
    ...common,
    value: draft,
    maxLength: field.maxLength ?? undefined,
    style: {
      ...style,
      fontSize,
      textAlign: (['left', 'center', 'right'] as const)[field.align] ?? 'left',
      letterSpacing: field.comb && field.maxLength
        ? `${Math.max((Number(style.width) / field.maxLength) - fontSize * 0.6, 0)}px`
        : undefined,
    },
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setDraft(event.target.value),
    onBlur: commit,
    onKeyDown: (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        setDraft(saved)
        ;(event.target as HTMLElement).blur()
      } else if (event.key === 'Enter' && !field.multiline) {
        event.preventDefault()
        ;(event.target as HTMLElement).blur()
      }
    },
  }
  return field.multiline
    ? <textarea {...shared} />
    : <input {...shared} type={field.password ? 'password' : 'text'} autoComplete="off" spellCheck />
}

function ChoiceControl({ field, style, common, onChange }: {
  field: FormField
  style: React.CSSProperties
  common: Record<string, unknown>
  onChange: Props['onChange']
}) {
  if (field.kind === 'combo' && field.editable) {
    const listId = `choices-${field.name}`
    return (
      <>
        <input
          {...common}
          list={listId}
          defaultValue={typeof field.value === 'string' ? field.value : ''}
          key={String(field.value)}
          style={style}
          onBlur={(event) => {
            if (event.target.value !== field.value) onChange(field.name, event.target.value)
          }}
        />
        <datalist id={listId}>
          {field.options.map((o) => <option key={o.export} value={o.export}>{o.display}</option>)}
        </datalist>
      </>
    )
  }
  const multiple = field.kind === 'list' && field.multiselect
  const value = multiple
    ? (Array.isArray(field.value) ? field.value : [])
    : (typeof field.value === 'string' ? field.value : '')
  return (
    <select
      {...common}
      multiple={multiple}
      size={field.kind === 'list' ? Math.max(2, field.options.length) : undefined}
      value={value}
      style={style}
      onChange={(event) => {
        const chosen = [...event.target.selectedOptions].map((o) => o.value)
        onChange(field.name, multiple ? chosen : chosen[0] ?? '')
      }}
    >
      {field.kind === 'combo' && !field.options.some((o) => o.export === value) && (
        <option value="">{'—'}</option>
      )}
      {field.options.map((o) => <option key={o.export} value={o.export}>{o.display}</option>)}
    </select>
  )
}
